import {
  CloseCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Flex, Input,
  Space, Spin,
  Typography,
} from 'antd';
import FileIcon from 'components/FileIcon';
import Layout from 'components/Layout';
import SelectAuthorizedEmail from 'components/SelectAuthorizedEmail';
import Table from 'components/Table';
import withGuard from 'components/withGuard';
import React, {
  useMemo, useRef, useState,
} from 'react';
import DebouncedInput from 'components/DebouncedInput';
import DateRangeInput from '../../components/DateRangeInput';
import TimelineDrawer from './TimelineDrawer';
import { getColumns, useFetcher } from './utils';

function Automations() {
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
  });
  const [count, setCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const resetPageRef = useRef(false);

  function handleFilterChange(payload) {
    resetPageRef.current = true;
    setFilters((prev) => ({ ...prev, ...payload }));
    setLoading(true);
  }

  const handleAutomationClick = (automation) => {
    setSelectedWorkflow(automation);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedWorkflow(null);
  };

  const columns = useMemo(() => getColumns({ hasStatus: true, hasTrigger: true }), []);

  const fetcher = useFetcher({
    filters,
    resetPageRef,
    setLoading,
    setWorkflows,
    setCount,
    queryParams: {
      auto_trigger: true,
    },
  });

  const hasActiveFiltersCount = Object.entries(filters).filter(([key, value]) => value != null && !['search'].includes(key)).length;

  return (
    <Layout header={(workflows.length > 0 || filters.search || loading || hasActiveFiltersCount > 0) && (
      <Layout.Header
        title={(
          <Flex align="center" justify="space-between" className="sdf">
            <div>
              Automations
              {' '}
              <small style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                (
                {count}
                )
              </small>
            </div>
          </Flex>
        )}
      />
    )}
    >
      {!filters.search && !workflows.length && !loading && hasActiveFiltersCount === 0 && (
        <Space direction="vertical" className="text-center justify-center w-full h-[90vh]">
          {loading && <Spin size="large" />}
          {!loading && (
            <Space direction="vertical" className="justify-center align-items-center w-full h-[90vh]">
              <FileIcon />
              <Typography.Paragraph>No automations yet!</Typography.Paragraph>
            </Space>
          )}
        </Space>
      )}
      <div
        style={{
          display: (!!workflows.length || !!filters.search || loading || hasActiveFiltersCount > 0) ? 'block' : 'none',
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <DebouncedInput
              placeholder="Automation name"
              value={filters.search}
              suffix={filters.search ? <CloseCircleOutlined onClick={() => handleFilterChange({ search: '' })} /> : <SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />}
              onChange={(search) => handleFilterChange({ search })}
              style={{ width: 422 }}
              size="large"
            />
            <Typography.Text>
              Automations run automatically. For specific needs, contact your admin.
            </Typography.Text>
          </div>
          <div className="gap-3 flex-wrap items-start flex">
            <DateRangeInput
              name="Date created"
              value={filters.run_date}
              onChange={(run_date) => handleFilterChange({ run_date })}
              hasAllTimeOption
            />
            <SelectAuthorizedEmail
              name="From"
              value={filters.email_from}
              onChange={(email_from) => handleFilterChange({ email_from })}
              className="w-[250px]"
              size="middle"
              labelInValue={false}
              placeholder="From"
            />
          </div>
        </div>
        <Table
          id="automations"
          fetchFn={fetcher}
          columns={columns}
          className="mt-8"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200, y: 600 }}
          onRow={(record) => ({
            onClick: () => handleAutomationClick(record),
          })}
          empty={(
            <Space direction="vertical" className="my-10 mx-5">
              <FileIcon />
              <Typography.Paragraph>No automations match your search criteria!</Typography.Paragraph>
            </Space>
          )}
        />
      </div>
      <TimelineDrawer
        workflowId={selectedWorkflow?.id}
        onclose={handleDrawerClose}
      />
    </Layout>
  );
}

export default withGuard(Automations, [], { redirect: true });
